import { Button, Spinner } from '@assembly-js/design-system';
import { useCallback, useState } from 'react';

import { useSuggestIconMutation } from 'src/services/api/applicationsApi';

/**
 * Shared "Suggest icon" inline action for any IconPicker: calls the AI
 * suggestion endpoint and swaps the trigger for a spinner while it runs.
 * Used by both the standalone icon picker and the app list's inline one so
 * they stay in sync rather than drifting into two suggest implementations.
 */
export const useSuggestIconAction = ({
  appName,
  onChange,
}: {
  appName: string;
  onChange: (iconName: string) => void;
}) => {
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestIcon] = useSuggestIconMutation();

  const renderSuggestAction = useCallback(
    (closePopover: () => void) => (
      <Button
        variant="secondary"
        size="sm"
        label="Suggest icon"
        className="h-8 whitespace-nowrap"
        onClick={() => {
          closePopover();
          setIsSuggesting(true);
          suggestIcon({ appName })
            .unwrap()
            .then((result) => {
              onChange(result.icon);
            })
            .catch(() => {
              // silently fail — icon remains unchanged
            })
            .finally(() => {
              setIsSuggesting(false);
            });
        }}
      />
    ),
    [appName, onChange, suggestIcon],
  );

  const suggestingIndicator = (className?: string) => (
    <div className={`flex items-center justify-center ${className ?? ''}`}>
      <Spinner size={5} />
    </div>
  );

  return { isSuggesting, renderSuggestAction, suggestingIndicator };
};
